import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { ErrorIngestPayload } from './error.types';

/**
 * Sanitização de PII para o ingest de erros (Fase 2 — Erros MVP).
 *
 * Regras:
 *  - Query strings com parâmetros sensíveis (token, senha, cpf, cnpj, cartão,
 *    api key, secrets...) são REMOVIDAS do URL antes de qualquer envio.
 *  - Stack truncada (default 8KB) preservando os frames iniciais.
 *  - NUNCA enviar body de request — apenas URL + método/estado + stack.
 *  - Mensagem limitada (2KB) para não carregar o ingest com corpo gigante.
 */

/** Parâmetros de query considerados sensíveis — removidos case-insensitive. */
export const SENSITIVE_QUERY_PARAMS: string[] = [
    'token', 'access_token', 'auth_token', 'id_token', 'refresh_token',
    'password', 'senha', 'passwd', 'pwd',
    'cpf', 'cnpj',
    'card', 'card_number', 'cardnumber', 'cvv', 'cvc', 'expiry', 'exp',
    'api_key', 'apikey', 'secret', 'client_secret', 'app_secret',
    'authorization', 'cookie', 'key',
];

/** Tamanho máximo do stack enviado (8KB — frames do topo são os relevantes). */
export const MAX_STACK_BYTES = 8192;

/** Tamanho máximo da mensagem enviada. */
export const MAX_MESSAGE_LENGTH = 2000;

/**
 * Remove os parâmetros de query sensíveis de uma URL (retorna a URL sem a
 * query inteira caso TODOS os params sejam sensíveis). URLs sem `?` voltam
 * intactas. O fragmento (`#`) é preservado.
 */
export function stripSensitiveQueryParams(url: string): string {
    if (!url)
    {
        return url;
    }

    const queryIndex = url.indexOf('?');
    if (queryIndex < 0)
    {
        return url;
    }

    const fragmentIndex = url.indexOf('#');
    const base = url.slice(0, queryIndex);
    const hasFragment = fragmentIndex >= 0;
    const queryEnd = hasFragment ? fragmentIndex : url.length;
    const query = url.slice(queryIndex + 1, queryEnd);
    const fragment = hasFragment ? url.slice(fragmentIndex) : '';

    const kept = query.split('&').filter((part) =>
    {
        if (!part)
        {
            return false;
        }
        const eq = part.indexOf('=');
        const key = (eq >= 0 ? part.slice(0, eq) : part).toLowerCase();
        return !SENSITIVE_QUERY_PARAMS.includes(key);
    });

    return kept.length > 0
        ? base + '?' + kept.join('&') + fragment
        : base + fragment;
}

/**
 * Trunca o stack preservando o início (frames mais próximos da origem).
 * Retorna `undefined` para entradas vazias.
 */
export function truncateStack(stack: string | null | undefined, maxBytes: number = MAX_STACK_BYTES): string | undefined
{
    if (!stack)
    {
        return undefined;
    }
    if (stack.length <= maxBytes)
    {
        return stack;
    }
    return `${stack.slice(0, maxBytes)}\n[stack truncado pelo app]`;
}

/** Campos aceitos pelo montador de payload (input normalizado). */
export interface ErrorIngestInput
{
    exceptionClass: string;
    message: string;
    stack?: string;
    url?: string;
    requestId?: string;
    component?: string;
}

/**
 * Monta o payload final do ingest: aplica truncamentos e só inclui campos
 * opcionais quando presentes. `userAgent` é adicionado automaticamente
 * quando o ambiente fornecer `navigator`.
 */
export function buildIngestPayload(input: ErrorIngestInput): ErrorIngestPayload
{
    const payload: ErrorIngestPayload = {
        exceptionClass: input.exceptionClass || 'UnknownError',
        message: (input.message || 'Erro desconhecido').slice(0, MAX_MESSAGE_LENGTH),
    };

    const stack = truncateStack(input.stack);
    if (stack)
    {
        payload.stack = stack;
    }

    const url = stripSensitiveQueryParams(input.url ?? '');
    if (url)
    {
        payload.url = url;
    }

    if (input.requestId)
    {
        payload.requestId = input.requestId;
    }

    if (input.component)
    {
        payload.component = input.component;
    }

    if (typeof navigator !== 'undefined' && navigator.userAgent)
    {
        payload.userAgent = navigator.userAgent;
    }

    return payload;
}

/**
 * Constrói o payload a partir de um erro HTTP 5xx + o request original.
 * Inclui o `X-Request-Id` ecoado pelo backend no response (quando presente)
 * e o URL SANITIZADO do request (query strings sensíveis removidas).
 */
export function buildHttpErrorPayload(error: HttpErrorResponse, request?: HttpRequest<any>): ErrorIngestPayload
{
    return buildIngestPayload({
        exceptionClass: error?.name ?? 'HttpErrorResponse',
        message: error?.message || `${error?.status ?? '??'} ${error?.statusText ?? ''}`.trim(),
        // HttpErrorResponse não é Error tipado — stack via cast defensivo.
        stack: (error as any)?.stack,
        url: request?.urlWithParams || error?.url || undefined,
        requestId: error?.headers?.get('X-Request-Id') ?? undefined,
    });
}